import React, { useState, useRef, useMemo, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import classNames from 'classnames'

import { INavbar, ISearch } from '@/types/cinematics'
import { useBrandAssets } from '@/hooks/cinematics/useBrandAssets'
import { useTranslation } from '@/localization/useTranslation'
import { SearchActor } from '@/actors/SearchActor'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { useUser } from '@/hooks/users/useUser'
import { useAuth } from '@/hooks/auth/useAuth'
import { useImage } from '@/hooks/images/useImage'
import { useHasSceneRoute } from '@/hooks/cinematics/useHasSceneRoute'
import { TEAM_ROUTES } from '@/utils/routes'
import { getFavoriteTeam } from '@/utils/teams'

import { useResponsiveItems } from './hooks/useResponsiveItems'
import { useMobileMenu } from './hooks/useMobileMenu'
import { DesktopNav } from './components/DesktopNav'
import { MobileNav } from './components/MobileNav'

import classes from './styles.module.scss'
import settings from '@/settings/public'

const appCode = settings.app.code ?? ''
const isTeamBrandingEnabled = settings.features?.teamBranding ?? false

interface INavbarProps {
  setNavbarProps: {
    hasNavbar?: boolean
    hasSearch?: boolean
    hasBackgroundImage?: boolean
  }
  globalNavbarProps: INavbar
  searchProps: ISearch
}

export const Navbar = (props: INavbarProps) => {
  const { globalNavbarProps, setNavbarProps, searchProps } = props
  const { logo, icon } = useBrandAssets()
  const { logout, isPendingLogout } = useAuth()
  const { profile, isLoggedIn } = useUser()
  const pathname = usePathname()
  const { t } = useTranslation()
  const { getImageUrl } = useImage({ cut: 'square', scale: '50' })

  const hasNavbar = setNavbarProps?.hasNavbar ?? true
  const hasSearch = setNavbarProps?.hasSearch ?? globalNavbarProps?.hasSearch ?? true
  const items = useMemo(() => globalNavbarProps?.items || [], [globalNavbarProps?.items])
  // The team entry points link to /favorite-team, so a brand whose cinematic
  // has no such scene yet (teamBranding shipped before the scene) shows none.
  const hasFavoriteTeamScene = useHasSceneRoute(TEAM_ROUTES.favoriteTeam)
  const favoriteTeam = useMemo(
    () => (isTeamBrandingEnabled && hasFavoriteTeamScene ? getFavoriteTeam(profile, appCode) : null),
    [profile, hasFavoriteTeamScene],
  )
  const favoriteTeamIconSrc = getImageUrl({ id: favoriteTeam?.flag }) ?? undefined

  // State
  const [isMobileSearchActive, setIsMobileSearchActive] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const searchRef = useRef<any>(null)
  const mobileSearchRef = useRef<any>(null)

  // Custom hooks
  const { visibleItems, collapsedItems, containerRef } = useResponsiveItems({ items })
  const mobileMenu = useMobileMenu()

  // Handlers
  const handleLogout = useCallback(() => {
    logout()
    mobileMenu.close()
  }, [logout, mobileMenu])

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const handleActiveSearch = useCallback(() => {
    setIsMobileSearchActive(true)
    searchRef.current?.setActive()
    mobileSearchRef.current?.setActive()
  }, [])

  const handleInactiveSearch = useCallback(() => {
    setIsMobileSearchActive(false)
    searchRef.current?.setInactive()
    mobileSearchRef.current?.setInactive()
  }, [])

  const handleHamburger = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    if (isMobileSearchActive) {
      handleInactiveSearch()
      return
    }
    mobileMenu.toggle()
  }, [isMobileSearchActive, handleInactiveSearch, mobileMenu])

  if (!hasNavbar) return null

  const logoSrc = logo
  const profileIconSrc = icon

  return (
    <>
      <nav className={classes.navbar}>
        <DesktopNav
          collapsedItems={collapsedItems}
          containerRef={containerRef}
          favoriteTeamIconAlt={favoriteTeam?.name}
          favoriteTeamIconSrc={favoriteTeamIconSrc}
          hasProfileLogo={globalNavbarProps?.hasProfileLogo}
          hasSearch={hasSearch}
          isLoggedIn={isLoggedIn}
          isPendingLogout={isPendingLogout}
          logoSrc={logoSrc}
          onLogout={handleLogout}
          onSearch={handleSearch}
          pathname={pathname}
          profileIconSrc={profileIconSrc}
          profileName={profile?.name?.full}
          searchRef={searchRef}
          searchTerm={searchTerm}
          visibleItems={visibleItems}
        />
        <MobileNav
          favoriteTeamIconAlt={favoriteTeam?.name}
          favoriteTeamIconSrc={favoriteTeamIconSrc}
          hamburgerRef={mobileMenu.hamburgerRef}
          hasSearch={hasSearch}
          isLoggedIn={isLoggedIn}
          isMenuActive={mobileMenu.isActive}
          isMobileSearchActive={isMobileSearchActive}
          items={items}
          logoSrc={logoSrc}
          menuRef={mobileMenu.menuRef}
          mobileSearchRef={mobileSearchRef}
          onCloseMenu={mobileMenu.close}
          onHamburgerClick={handleHamburger}
          onLogout={handleLogout}
          onSearch={handleSearch}
          onSearchClick={handleActiveSearch}
          pathname={pathname}
          profileName={profile?.name?.full}
          searchTerm={searchTerm}
        />
      </nav>
      {/* Search Backdrop */}
      {hasSearch &&
        <div
          className={classNames({
            [classes.backdrop]: true,
            [classes.backdrop__active]: searchTerm,
          })}
        >
          <SearchActor
            searchTerm={searchTerm}
            clearSearch={handleInactiveSearch}
            searchProps={searchProps}
          />
        </div>
      }
      <LoadingOverlay isLoading={isPendingLogout} />
    </>
  )
}